import React from 'react';
import ContentLinesEditor from './ContentLinesEditor';
import TableColumnEditor from './TableColumnEditor';

const A4_WIDTH = 595;
const A4_HEIGHT = 842;

const PositionInput = ({ label, value, max, onChange }) => (
  <div style={{ flex: 1, minWidth: '80px' }}>
    <label style={{ display: 'block', fontWeight: 600, marginBottom: '2px', fontSize: '11px', color: 'var(--text-secondary)' }}>
      {label}
    </label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <input
        type="number"
        value={value}
        min={0}
        max={max}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        style={{
          width: '55px', padding: '3px 4px', border: '1px solid var(--border)',
          borderRadius: '3px', fontSize: '11px', textAlign: 'center',
        }}
      />
      <input
        type="range"
        value={value}
        min={0}
        max={max}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        style={{ flex: 1, height: '16px', cursor: 'pointer' }}
      />
    </div>
  </div>
);

const QuickPositionBar = ({ field, updateField }) => {
  const centerH = () => updateField(field.id, { x: Math.round((A4_WIDTH - field.width) / 2) });
  const alignLeft = () => updateField(field.id, { x: 50 }); // Left margin
  const alignRight = () => updateField(field.id, { x: A4_WIDTH - 50 - field.width }); // Right margin
  const fullWidth = () => updateField(field.id, { x: 50, width: A4_WIDTH - 100 });

  const btnStyle = {
    padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)',
  };

  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      <button onClick={alignLeft} style={btnStyle} title="Do lewego marginesu (x=50)">
        &#x25C0; Lewo
      </button>
      <button onClick={centerH} style={btnStyle} title="Wysrodkuj w poziomie">
        &#x25C6; Srodek
      </button>
      <button onClick={alignRight} style={btnStyle} title="Do prawego marginesu">
        Prawo &#x25B6;
      </button>
      <button onClick={fullWidth} style={btnStyle} title="Pelna szerokosc (z marginesami)">
        &#x2194; Pelna szer.
      </button>
    </div>
  );
};

const FieldItem = ({
  field,
  index,
  totalFields,
  isExpanded,
  isSelected,
  toggleFieldExpansion,
  moveFieldUp,
  moveFieldDown,
  removeField,
  updateField,
  updateContentLine,
  toggleLineBold,
  removeContentLine,
  addContentLine,
  toggleVariablesPanel,
  showVariables,
  insertVariable,
  insertVariableNewLine,
  moveFieldToPosition,
  editingFields,
  moveColumnUp,
  moveColumnDown,
}) => (
  <div style={{
    padding: '8px',
    backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.08)' : 'var(--bg)',
    border: isSelected ? '2px solid #3b82f6' : '1px solid var(--border)',
    borderRadius: '4px',
    marginBottom: '6px',
  }}>
    {/* Header */}
    <div
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      onClick={() => toggleFieldExpansion(field.id)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: '12px', flexShrink: 0 }}>{isExpanded ? '▼' : '▶'}</span>
        <span style={{
          fontSize: '10px', padding: '1px 5px', borderRadius: '3px', flexShrink: 0,
          backgroundColor: field.type === 'table' ? '#d1fae5' : field.type === 'title' ? '#ede9fe' : '#dbeafe',
          color: field.type === 'table' ? '#065f46' : field.type === 'title' ? '#5b21b6' : '#1e40af',
          fontWeight: 600, textTransform: 'uppercase',
        }}>
          {field.type}
        </span>
        <span style={{ fontSize: '12px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {field.content ? field.content.substring(0, 35) + (field.content.length > 35 ? '...' : '') : 'Brak tresci'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
        <button
          onClick={(e) => { e.stopPropagation(); moveFieldUp(field.id); }}
          disabled={index === 0}
          style={{
            backgroundColor: 'transparent', color: index === 0 ? 'var(--border)' : 'var(--text)',
            border: 'none', padding: '2px 5px', cursor: index === 0 ? 'default' : 'pointer', fontSize: '12px',
          }}
          title="Przesun w gore"
        >&#x2191;</button>
        <button
          onClick={(e) => { e.stopPropagation(); moveFieldDown(field.id); }}
          disabled={index === totalFields - 1}
          style={{
            backgroundColor: 'transparent', color: index === totalFields - 1 ? 'var(--border)' : 'var(--text)',
            border: 'none', padding: '2px 5px', cursor: index === totalFields - 1 ? 'default' : 'pointer', fontSize: '12px',
          }}
          title="Przesun w dol"
        >&#x2193;</button>
        <button
          onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
          style={{
            backgroundColor: 'transparent', color: 'var(--danger)',
            border: 'none', padding: '2px 5px', cursor: 'pointer', fontSize: '12px',
          }}
          title="Usun pole"
        >&#x2716;</button>
      </div>
    </div>

    {/* Expanded content */}
    {isExpanded && (
      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
        {/* Content + Type row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', marginBottom: '8px' }}>
          <ContentLinesEditor
            field={field}
            updateContentLine={updateContentLine}
            toggleLineBold={toggleLineBold}
            removeContentLine={removeContentLine}
            updateField={updateField}
            toggleVariablesPanel={toggleVariablesPanel}
            addContentLine={addContentLine}
            showVariables={showVariables}
            insertVariable={insertVariable}
            insertVariableNewLine={insertVariableNewLine}
          />
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '2px', fontSize: '11px', color: 'var(--text-secondary)' }}>Typ:</label>
            <select
              value={field.type}
              onChange={(e) => updateField(field.id, { type: e.target.value })}
              style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
            >
              <option value="text">Tekst</option>
              <option value="title">Tytul</option>
              <option value="data">Dane</option>
              <option value="table">Tabela</option>
            </select>
          </div>
        </div>

        {/* Quick position bar */}
        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '3px', fontSize: '11px', color: 'var(--text-secondary)' }}>
            Szybkie pozycjonowanie:
          </label>
          <QuickPositionBar field={field} updateField={updateField} />
        </div>

        {/* Position inputs with sliders */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '8px' }}>
          <PositionInput
            label="X (poziomo):"
            value={field.x}
            max={A4_WIDTH}
            onChange={(v) => updateField(field.id, { x: v })}
          />
          <PositionInput
            label="Y (pionowo):"
            value={field.y}
            max={A4_HEIGHT}
            onChange={(v) => updateField(field.id, { y: v })}
          />
          <PositionInput
            label="Szerokosc:"
            value={field.width}
            max={A4_WIDTH}
            onChange={(v) => updateField(field.id, { width: v })}
          />
          <PositionInput
            label="Wysokosc:"
            value={field.height}
            max={A4_HEIGHT}
            onChange={(v) => updateField(field.id, { height: v })}
          />
        </div>

        {/* Style controls */}
        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '3px', fontSize: '11px', color: 'var(--text-secondary)' }}>
            Styl:
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Rozmiar:</label>
              <input
                type="number"
                value={field.style?.fontSize || 12}
                onChange={(e) => updateField(field.id, { style: { ...field.style, fontSize: parseInt(e.target.value) || 12 } })}
                style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Grubosc:</label>
              <select
                value={field.style?.fontWeight || 'normal'}
                onChange={(e) => updateField(field.id, { style: { ...field.style, fontWeight: e.target.value } })}
                style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Wyrownanie:</label>
              <select
                value={field.style?.textAlign || 'left'}
                onChange={(e) => updateField(field.id, { style: { ...field.style, textAlign: e.target.value } })}
                style={{ width: '100%', padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
              >
                <option value="left">Lewo</option>
                <option value="center">Srodek</option>
                <option value="right">Prawo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table columns editor */}
        {field.type === 'table' && (
          <TableColumnEditor
            field={field}
            updateField={updateField}
            moveColumnUp={moveColumnUp}
            moveColumnDown={moveColumnDown}
          />
        )}

        {/* Field order selector */}
        <div style={{ padding: '6px', backgroundColor: 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              Kolejnosc:
            </label>
            <select
              value={index}
              onChange={(e) => moveFieldToPosition(field.id, parseInt(e.target.value))}
              style={{ flex: 1, padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '3px', fontSize: '11px' }}
            >
              {editingFields.map((_, selectIndex) => (
                <option key={selectIndex} value={selectIndex}>
                  {selectIndex + 1}. pozycja
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    )}
  </div>
);

export default FieldItem;
